/** Small shared building blocks: page chrome, states, meters, formatting. */
import type { ReactNode } from 'react';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ReportProblemRounded from '@mui/icons-material/ReportProblemRounded';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { ApiError } from '@/api/client';
import { radius, semantic, status as statusColor, surface, tint } from '@/theme/theme';

export { ColorModeToggle } from '@/components/common/ColorModeToggle';
export {
  FactsStrip,
  FilterBar,
  KpiRow,
  ProvenanceStamp,
  revealRowActions,
  ROW_ACTIONS,
  RowLink,
} from '@/components/common/patterns';
export type { Fact, Kpi } from '@/components/common/patterns';

// --- formatting -------------------------------------------------------------

const DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const DATE_SHORT = new Intl.DateTimeFormat('en-US', { month: 'numeric', day: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});
const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const MONEY_WHOLE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return DATE.format(new Date(`${value.slice(0, 10)}T12:00:00`));
}

export function formatShortDate(value: string | null | undefined): string {
  if (!value) return '';
  return DATE_SHORT.format(new Date(`${value.slice(0, 10)}T12:00:00`));
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return DATE_TIME.format(new Date(value));
}

export function formatMoney(value: number | null | undefined, whole = false): string {
  if (value === null || value === undefined) return '—';
  return (whole ? MONEY_WHOLE : MONEY).format(value);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Days remaining, stated as a phrase rather than a bare number. */
export function describeDaysRemaining(days: number | null | undefined): string {
  if (days === null || days === undefined) return 'No due date';
  if (days < 0) return `${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `${days} days left`;
}

// --- page chrome ------------------------------------------------------------

export interface Crumb {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  title: string;
  /** One line under the title. Names the number rather than saying "significant". */
  description?: string;
  crumbs?: Crumb[];
  actions?: ReactNode;
  backTo?: string;
  meta?: ReactNode;
}

export function PageHeader({
  title,
  description,
  crumbs,
  actions,
  backTo,
  meta,
}: PageHeaderProps): JSX.Element {
  return (
    <Box sx={{ mb: 6 }}>
      {crumbs && crumbs.length > 0 ? (
        <Breadcrumbs sx={{ mb: 2, fontSize: '0.8125rem' }} aria-label="Breadcrumb">
          {crumbs.map((crumb) =>
            crumb.to ? (
              <Link key={crumb.label} component={RouterLink} to={crumb.to}>
                {crumb.label}
              </Link>
            ) : (
              <Typography key={crumb.label} variant="body2" color="text.secondary">
                {crumb.label}
              </Typography>
            ),
          )}
        </Breadcrumbs>
      ) : null}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {backTo ? (
              <IconButton
                component={RouterLink}
                to={backTo}
                size="small"
                aria-label="Go back"
                sx={{ ml: -1 }}
              >
                <ArrowBackRounded fontSize="small" />
              </IconButton>
            ) : null}
            <Typography variant="h2" component="h1" sx={{ wordBreak: 'break-word' }}>
              {title}
            </Typography>
          </Stack>
          {description ? (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: '68ch' }}>
              {description}
            </Typography>
          ) : null}
          {meta ? <Box sx={{ mt: 2 }}>{meta}</Box> : null}
        </Box>
        {actions ? (
          <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
            {actions}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}

// --- states -----------------------------------------------------------------

interface EmptyStateProps {
  title: string;
  /** Says what to do next, not just that there is nothing here. */
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}

/**
 * The frame an empty, loading or failed area sits in (guide 1.0.0, app-states): the page around it
 * stays, and the area itself is a dashed box with its words centred, so it reads as a place
 * something will be rather than as a broken panel.
 */
const STATE_FRAME = {
  textAlign: 'center',
  padding: '40px 24px',
  border: `1px dashed ${semantic.borderStrong}`,
  borderRadius: radius.md,
  backgroundColor: surface.page,
} as const;

function StateWords({ title, text }: { title: ReactNode; text?: ReactNode }): JSX.Element {
  return (
    <>
      <Typography component="p" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {text ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '26rem', mx: 'auto' }}>
          {text}
        </Typography>
      ) : null}
    </>
  );
}

export function EmptyState({ title, description, action, icon }: EmptyStateProps): JSX.Element {
  return (
    <Box sx={STATE_FRAME}>
      {icon ? (
        <Box sx={{ color: semantic.inkMuted, mb: 2, '& svg': { fontSize: 32 } }}>{icon}</Box>
      ) : null}
      <StateWords title={title} text={description} />
      {action ? <Box sx={{ mt: 5 }}>{action}</Box> : null}
    </Box>
  );
}

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}

export function ErrorState({ error, onRetry, title }: ErrorStateProps): JSX.Element {
  const isApi = error instanceof ApiError;
  const message = isApi ? error.message : 'Something went wrong loading this page.';
  const forbidden = isApi && error.isForbidden;
  // What happened, what to do, and the reassurance (guide: app-states). These are failures to load,
  // so nothing was changed.
  return (
    <Box sx={STATE_FRAME} role="alert">
      <Box sx={{ color: statusColor.critical, mb: 2, '& svg': { fontSize: 32 } }}>
        <ReportProblemRounded />
      </Box>
      <StateWords
        title={title ?? (forbidden ? 'You do not have access to this' : 'Could not load')}
        text={
          <>
            {message} {forbidden ? 'Ask an administrator for access.' : 'Nothing was changed.'}
          </>
        }
      />
      {isApi && error.correlationId ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Reference {error.correlationId.slice(0, 8)}
        </Typography>
      ) : null}
      {onRetry ? (
        <Button onClick={onRetry} variant="outlined" sx={{ mt: 5 }}>
          Try again
        </Button>
      ) : null}
    </Box>
  );
}

export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }): JSX.Element {
  return (
    <Paper>
      <Table>
        <TableBody>
          {Array.from({ length: rows }, (_, rowIndex) => (
            <TableRow key={rowIndex}>
              {Array.from({ length: columns }, (_, columnIndex) => (
                <TableCell key={columnIndex}>
                  {/* Pill bars at 100 / 80 / 60%, as the guide's loading state draws them. */}
                  <Skeleton variant="text" width={`${[100, 80, 60][rowIndex % 3]}%`} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export function CardSkeleton({ height = 120 }: { height?: number }): JSX.Element {
  return <Skeleton variant="rounded" height={height} sx={{ borderRadius: radius.md }} />;
}


// --- dialogs ----------------------------------------------------------------

/**
 * A label's first word as the action under way: "Delete" → "Deleting", "Close RFP" → "Closing RFP".
 * The labels are the app's own imperative verbs, so the plain English rules cover them: a silent
 * final e drops ("Remove" → "Removing"), and nothing here doubles a consonant.
 */
export function inProgress(label: string): string {
  const [verb = '', ...rest] = label.split(' ');
  const stem = /[^e]e$/i.test(verb) ? verb.slice(0, -1) : verb;
  return [`${stem}ing`, ...rest].join(' ');
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** States the consequence in plain words, with the number where there is one. */
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  /** What the confirm button says while it works. Defaults to the confirm label's verb as it
   *  happens — "Remove contact" works as "Removing contact" — because the guide keeps the verb
   *  through the flow rather than a generic "Working…". */
  busyLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  busyLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: semantic.ink }}>{description}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 6, pb: 5 }}>
        <Button onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={busy}
          // Destructive in the guide: red with white text (4.72:1), one translucent overlay on hover.
          sx={
            destructive
              ? {
                  backgroundColor: statusColor.critical,
                  color: semantic.inkOnBrand,
                  '&:hover, &:focus-visible': {
                    backgroundColor: statusColor.critical,
                    color: semantic.inkOnBrand,
                    boxShadow: `inset 0 0 0 999px ${tint('color-semantic-ink', 0.1)}`,
                  },
                }
              : undefined
          }
        >
          {busy ? (busyLabel ?? inProgress(confirmLabel)) : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** A definition list for the detail panels, so labels and values line up everywhere. */
export function FieldGrid({
  items,
}: {
  items: Array<{ label: string; value: ReactNode }>;
}): JSX.Element {
  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
        columnGap: 6,
        rowGap: 2.5,
        m: 0,
      }}
    >
      {items.map((item) => (
        <Box key={item.label} sx={{ display: 'contents' }}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {item.label}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 500 }}>
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
